import type { Dict } from '../i18n';

const dict: Dict = {
  en: {
    // Brand pane
    tagline: 'Order management & automation',
    taglineSub: 'Place orders, follow them through proforma, payment, invoice and shipping.',
    signinAgreement: 'By signing in you agree to our',
    privacyLink: 'Privacy Policy & DPA',

    // Credentials step
    credentialsHeading: 'Sign in',
    credentialsHint: 'Use your staff or agent account.',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    signingIn: 'Signing in…',
    signinBtn: 'Sign in →',

    // Set-password step (first login with a temporary password)
    setPwHeading: 'Set your password',
    setPwHint: 'Your account uses a temporary password. Choose a new one to continue.',
    newPasswordLabel: 'New password',
    confirmPasswordLabel: 'Confirm new password',
    pwPolicyHint: 'At least 12 characters, with an upper- and lower-case letter and a digit.',
    pwTooShort: 'Password must be at least 12 characters.',
    pwNoLower: 'Password must contain a lowercase letter.',
    pwNoUpper: 'Password must contain an uppercase letter.',
    pwNoDigit: 'Password must contain a digit.',
    pwMismatch: 'Passwords do not match.',
    savingPw: 'Saving…',
    setPwBtn: 'Set password →',

    // TOTP step
    totpHeading: 'Two-factor code',
    totpHint: 'Enter the 6-digit code from your authenticator app.',
    verifying: 'Verifying…',
    verifyBtn: 'Verify →',
    backBtn: '← Back',

    // Enrollment step
    enrollHeading: 'Set up two-factor auth',
    enrollHint:
      '2FA is required. Scan the QR (or enter the secret) in your authenticator app, then confirm the 6-digit code.',
    secretLabel: 'Secret',
    scanQrLabel: 'Scan this QR in your authenticator app',
    orEnterKeyManually: 'or enter this key manually',
    recoveryCodesLabel: 'Recovery codes — store these now',
    confirming: 'Confirming…',
    confirmBtn: 'Confirm & sign in →',

    // TotpInput
    sixDigitLabel: '6-digit code',

    // Footer
    readOur: 'Read our',
    privacyLinkDraft: 'Privacy Policy & DPA (draft)',

    // Fallback error
    genericError: 'Something went wrong. Please try again.',
  },
  es: {
    // Brand pane
    tagline: 'Gestión y automatización de pedidos',
    taglineSub:
      'Crea pedidos y síguelos desde la proforma, el pago, la factura hasta el envío.',
    signinAgreement: 'Al iniciar sesión aceptas nuestra',
    privacyLink: 'Política de privacidad y DPA',

    // Credentials step
    credentialsHeading: 'Iniciar sesión',
    credentialsHint: 'Usa tu cuenta de personal o agente.',
    emailLabel: 'Correo electrónico',
    passwordLabel: 'Contraseña',
    signingIn: 'Iniciando sesión…',
    signinBtn: 'Iniciar sesión →',

    // Set-password step (first login with a temporary password)
    setPwHeading: 'Establece tu contraseña',
    setPwHint: 'Tu cuenta usa una contraseña temporal. Elige una nueva para continuar.',
    newPasswordLabel: 'Nueva contraseña',
    confirmPasswordLabel: 'Confirmar nueva contraseña',
    pwPolicyHint: 'Al menos 12 caracteres, con una letra mayúscula, una minúscula y un dígito.',
    pwTooShort: 'La contraseña debe tener al menos 12 caracteres.',
    pwNoLower: 'La contraseña debe contener una letra minúscula.',
    pwNoUpper: 'La contraseña debe contener una letra mayúscula.',
    pwNoDigit: 'La contraseña debe contener un dígito.',
    pwMismatch: 'Las contraseñas no coinciden.',
    savingPw: 'Guardando…',
    setPwBtn: 'Establecer contraseña →',

    // TOTP step
    totpHeading: 'Código de doble factor',
    totpHint: 'Introduce el código de 6 dígitos de tu aplicación de autenticación.',
    verifying: 'Verificando…',
    verifyBtn: 'Verificar →',
    backBtn: '← Atrás',

    // Enrollment step
    enrollHeading: 'Configurar el doble factor',
    enrollHint:
      'El 2FA es obligatorio. Escanea el código QR (o introduce el secreto) en tu aplicación de autenticación y confirma el código de 6 dígitos.',
    secretLabel: 'Secreto',
    scanQrLabel: 'Escanea este QR en tu app de autenticación',
    orEnterKeyManually: 'o introduce esta clave manualmente',
    recoveryCodesLabel: 'Códigos de recuperación — guárdalos ahora',
    confirming: 'Confirmando…',
    confirmBtn: 'Confirmar e iniciar sesión →',

    // TotpInput
    sixDigitLabel: 'Código de 6 dígitos',

    // Footer
    readOur: 'Lee nuestra',
    privacyLinkDraft: 'Política de privacidad y DPA (borrador)',

    // Fallback error
    genericError: 'Algo salió mal. Por favor, inténtalo de nuevo.',
  },
  de: {
    // Brand pane
    tagline: 'Bestellverwaltung & Automatisierung',
    taglineSub:
      'Bestellungen aufgeben und von Proforma über Zahlung und Rechnung bis zum Versand verfolgen.',
    signinAgreement: 'Mit der Anmeldung stimmen Sie unserer',
    privacyLink: 'Datenschutzerklärung & AVV',

    // Credentials step
    credentialsHeading: 'Anmelden',
    credentialsHint: 'Verwenden Sie Ihr Mitarbeiter- oder Agentenkonto.',
    emailLabel: 'E-Mail',
    passwordLabel: 'Passwort',
    signingIn: 'Anmeldung…',
    signinBtn: 'Anmelden →',

    // Set-password step (first login with a temporary password)
    setPwHeading: 'Passwort festlegen',
    setPwHint: 'Ihr Konto verwendet ein temporäres Passwort. Wählen Sie ein neues, um fortzufahren.',
    newPasswordLabel: 'Neues Passwort',
    confirmPasswordLabel: 'Neues Passwort bestätigen',
    pwPolicyHint: 'Mindestens 12 Zeichen, mit einem Groß- und einem Kleinbuchstaben sowie einer Ziffer.',
    pwTooShort: 'Das Passwort muss mindestens 12 Zeichen lang sein.',
    pwNoLower: 'Das Passwort muss einen Kleinbuchstaben enthalten.',
    pwNoUpper: 'Das Passwort muss einen Großbuchstaben enthalten.',
    pwNoDigit: 'Das Passwort muss eine Ziffer enthalten.',
    pwMismatch: 'Die Passwörter stimmen nicht überein.',
    savingPw: 'Wird gespeichert…',
    setPwBtn: 'Passwort festlegen →',

    // TOTP step
    totpHeading: 'Zwei-Faktor-Code',
    totpHint: 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.',
    verifying: 'Wird geprüft…',
    verifyBtn: 'Bestätigen →',
    backBtn: '← Zurück',

    // Enrollment step
    enrollHeading: 'Zwei-Faktor-Authentifizierung einrichten',
    enrollHint:
      '2FA ist erforderlich. Scannen Sie den QR-Code (oder geben Sie das Geheimnis ein) in Ihrer Authenticator-App und bestätigen Sie dann den 6-stelligen Code.',
    secretLabel: 'Geheimnis',
    scanQrLabel: 'Scannen Sie diesen QR-Code in Ihrer Authenticator-App',
    orEnterKeyManually: 'oder geben Sie diesen Schlüssel manuell ein',
    recoveryCodesLabel: 'Wiederherstellungscodes — bewahren Sie diese jetzt auf',
    confirming: 'Wird bestätigt…',
    confirmBtn: 'Bestätigen & anmelden →',

    // TotpInput
    sixDigitLabel: '6-stelliger Code',

    // Footer
    readOur: 'Lesen Sie unsere',
    privacyLinkDraft: 'Datenschutzerklärung & AVV (Entwurf)',

    // Fallback error
    genericError: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
  },
};

export default dict;
